/**
 * Spec 34 — pure merger from (active uploads, inventory) to the
 * unified DocumentRow list the Library Hub renders.
 *
 * The merger is the load-bearing piece of the new Hub: it owns
 * every state-machine transition documented in
 * docs/specs/34-library-hub-unified-row.md §"State machine". Pure
 * function: same inputs → same output. No I/O, no SDK calls, no
 * implicit state. Tested in isolation with a fixture covering
 * every edge.
 *
 * Key correlation rules:
 *  1. Once an upload returns 2xx with a doc_id, that doc_id is the
 *     stable identity. The slot's ``doc_id`` field is set, and
 *     subsequent inventory polls merge by doc_id.
 *  2. While the upload is in flight or just returned but the
 *     inventory hasn't caught up, slots are keyed by filename. The
 *     merger never produces two rows for the same doc — once a
 *     filename matches an inventory entry, the slot is consumed.
 *  3. ``failed_upload`` slots stay visible (with their filename)
 *     until the writer dismisses them; they don't inherit
 *     inventory state because the upload never reached upstream.
 */

import type { ResolvedInventoryDTO } from "../../api/client";
import type { DocumentInfo } from "../../types";
import { inventoryDtoToView } from "./inventoryAdapter";
import type {
  DocumentRow,
  InventoryClassificationLite,
  InventorySourceClassificationLite,
  InventoryView,
  ProcessingStatus,
  SourceClassificationView,
  UploadSlot,
} from "./types";

const TERMINAL_DONE = "Done";
const FAILED_PREFIX = "Failed:";

/**
 * Compute the lifecycle state for one doc from the upstream status
 * string alone.
 *
 * Status mapping: ``Failed:*`` → failed_processing; anything that
 * isn't ``Done`` (Pending / Parsing / null) → indexing (still
 * working); ``Done`` → ready. The ``parsed`` flag and whether an
 * inventory classification exists are accepted for backwards
 * compatibility but no longer affect the result — ``Done`` is the
 * authoritative "processing finished" signal.
 */
export function deriveProcessingStatus(opts: {
  hasInventoryClassification?: boolean;
  upstreamStatus: string | null;
  parsed?: boolean | null;
}): ProcessingStatus {
  const { upstreamStatus } = opts;
  if (upstreamStatus?.startsWith(FAILED_PREFIX)) {
    return "failed_processing";
  }
  if (upstreamStatus !== TERMINAL_DONE) {
    // Pending / Parsing / null — all map to indexing from the
    // writer's POV ("Sangnan is still chewing on this").
    return "indexing";
  }
  // upstreamStatus === "Done" from here on. The upstream "Done" IS
  // the authoritative signal that processing finished — that's all we
  // track. The ``parsed`` flag is intentionally ignored: it doesn't
  // reliably reflect usability (the server coerces non-bool/absent
  // values to null, and "Done + parsed=false" is a normal state), and
  // gating on it produced confusing "Partial" labels on healthy docs.
  return "ready";
}

/**
 * Map raw upload-slot state → ProcessingStatus. Used when the slot
 * hasn't been merged with inventory yet (e.g. the upload just
 * finished but the next inventory poll hasn't returned).
 */
function uploadSlotStatus(slot: UploadSlot): ProcessingStatus {
  switch (slot.status) {
    case "waiting":
    case "uploading":
      return "uploading";
    case "failed_upload":
      return "failed_upload";
    case "uploaded":
      // POST returned 2xx but inventory hasn't caught up yet — show
      // ``indexing`` as a placeholder; next inventory poll will
      // settle the row.
      return "indexing";
  }
}

/**
 * Lift one inventory entry to the row shape, optionally merging an
 * upload slot's state (for the "just uploaded" transition window).
 */
function rowFromInventory(
  doc: DocumentInfo,
  classification: InventoryClassificationLite | undefined,
  sourceClassification: InventorySourceClassificationLite | undefined,
  matchingSlot: UploadSlot | undefined,
): DocumentRow {
  const upstreamStatus = classification?.processing_status ?? doc.processing_status ?? null;
  const parsed = classification?.parsed ?? doc.parsed ?? null;
  const status = deriveProcessingStatus({
    hasInventoryClassification: !!classification,
    upstreamStatus,
    parsed,
  });
  const sourceView = sourceClassification ? buildSourceView(sourceClassification) : null;
  // ``low`` flag — confidence pill triggers the slow pulse.
  const low =
    classification?.confidence === "low" ||
    (sourceView?.source_doctype_confidence != null && sourceView.source_doctype_confidence < 0.6);
  const blocking =
    sourceView?.source_doctype === "Other" &&
    (classification?.doc_type === "Other" || classification?.doc_type === "?" || !classification?.doc_type);
  return {
    doc_id: doc.id,
    filename: doc.name,
    processing_status: status,
    upstream_status: upstreamStatus,
    parsed,
    upload_progress_percent: matchingSlot?.upload_progress_percent ?? null,
    doc_type: classification?.doc_type ?? null,
    classification_confidence: classification?.confidence ?? null,
    source_classification: sourceView,
    verified: !!classification?.verified,
    verified_by: classification?.verified_by,
    verified_at: classification?.verified_at,
    blocking,
    low,
    label_params: classification?.label_params ?? null,
  };
}

/**
 * Convert a slot that hasn't matched any inventory entry into a
 * row — used for "uploading" / "just uploaded but not in inventory
 * yet" / "failed_upload".
 */
function rowFromSlotOnly(slot: UploadSlot): DocumentRow {
  return {
    doc_id: slot.doc_id,
    filename: slot.filename,
    processing_status: uploadSlotStatus(slot),
    upstream_status: null,
    parsed: null,
    upload_progress_percent: slot.upload_progress_percent,
    doc_type: null,
    classification_confidence: null,
    source_classification: null,
    verified: false,
    upload_error: slot.error_message,
    client_id: slot.client_id,
  };
}

/**
 * Convert a Spec 32 source-classification record into the
 * UI-facing view. Splits out the ``confidence`` dict's
 * source_doctype key as a separate ``source_doctype_confidence``
 * for ergonomics; keeps the per-tag confidences alongside the
 * tags.
 */
function buildSourceView(s: InventorySourceClassificationLite): SourceClassificationView {
  const { source_doctype: sdConf, ...tagConfs } = s.confidence;
  return {
    source_doctype: s.source_doctype,
    source_doctype_confidence: typeof sdConf === "number" ? sdConf : null,
    tags: { ...s.tags },
    tag_confidences: { ...tagConfs },
    rationale: s.rationale || null,
    error: s.error,
  };
}

/**
 * Merge active upload slots with the inventory view to produce one
 * row per doc.
 *
 * Algorithm:
 *  1. Index inventory by doc_id (1 doc → 1 row).
 *  2. For each inventory doc, see if an upload slot matches by
 *     doc_id (preferred) OR filename (fallback while the slot's
 *     doc_id hasn't been set yet). Build a row from the inventory,
 *     enriching with slot progress when applicable.
 *  3. For each upload slot that didn't match any inventory entry,
 *     emit a slot-only row (``uploading`` / ``failed_upload`` /
 *     transient ``indexing``).
 *
 * The output preserves inventory order; rows from slot-only state
 * are appended at the end. Caller (``HubList``) applies the visual
 * sort that puts in-flight + failed rows at the top.
 */
export function mergeUploadsAndInventory(opts: {
  uploads: UploadSlot[];
  inventory: InventoryView;
}): DocumentRow[] {
  const { uploads, inventory } = opts;

  const classificationByDocId = new Map<string, InventoryClassificationLite>();
  for (const c of inventory.classifications) {
    classificationByDocId.set(c.doc_id, c);
  }
  const sourceByDocId = new Map<string, InventorySourceClassificationLite>();
  for (const s of inventory.source_classifications) {
    sourceByDocId.set(s.doc_id, s);
  }

  // Slots indexed by doc_id and (separately) by filename. A name can
  // carry several slots (the same file sent again, or deleted and sent
  // again); every one of them pairs with the inventory row, so none is
  // left over to render as a second, "indexing" copy.
  const slotsByDocId = new Map<string, UploadSlot>();
  const slotsByFilename = new Map<string, UploadSlot[]>();
  for (const s of uploads) {
    if (s.doc_id) slotsByDocId.set(s.doc_id, s);
    const same = slotsByFilename.get(s.filename);
    if (same) same.push(s);
    else slotsByFilename.set(s.filename, [s]);
  }
  const consumedSlotClientIds = new Set<string>();

  const rows: DocumentRow[] = [];

  for (const doc of inventory.documents) {
    const byName = slotsByFilename.get(doc.name) ?? [];
    const matchingSlot: UploadSlot | undefined = slotsByDocId.get(doc.id) ?? byName[byName.length - 1];
    if (matchingSlot) consumedSlotClientIds.add(matchingSlot.client_id);
    for (const s of byName) consumedSlotClientIds.add(s.client_id);
    rows.push(rowFromInventory(doc, classificationByDocId.get(doc.id), sourceByDocId.get(doc.id), matchingSlot));
  }

  // Slot-only rows — uploads that haven't reached inventory yet. (A
  // deleted doc's slot is dropped by the delete itself, not here: an
  // uploaded slot missing from the inventory is usually just early.)
  for (const s of uploads) {
    if (consumedSlotClientIds.has(s.client_id)) continue;
    rows.push(rowFromSlotOnly(s));
  }

  return rows;
}

/**
 * One blocking document — surfaced in the draft-gate message so the
 * writer knows exactly which source is holding up the run.
 */
export interface BlockingDoc {
  filename: string;
  status: ProcessingStatus;
}

/** A table create_artefact built and saved (csr_sim.artefacts.persist names them ``derived-…``). */
export function isDerivedFile(filename: string | null | undefined): boolean {
  return (filename ?? "").startsWith("derived-");
}

/**
 * Whether a source library is ready to draft from: every document
 * must be fully processed (``processing_status === "ready"``). A run
 * started while a doc is still uploading/parsing/classifying, has
 * failed, or is structurally-analysed-but-text-not-indexed
 * (``limited``) would retrieve nothing useful from that doc.
 *
 * Reuses the same pure merger the Hub UI renders from, so the gate
 * and the on-screen row states can never disagree. A ``null``
 * inventory (not fetched yet) is treated as ready — we don't have a
 * signal to block on, and false-blocking would trap the writer.
 *
 * Derived tables (``derived-…``, saved back into the library by earlier
 * runs) are skipped: drafting never reads them, and one that failed
 * parsing made the whole library undraftable.
 */
export function libraryDraftReadiness(inventory: ResolvedInventoryDTO | null): {
  ready: boolean;
  blocking: BlockingDoc[];
} {
  if (!inventory) return { ready: true, blocking: [] };
  const rows = mergeUploadsAndInventory({
    uploads: [],
    inventory: inventoryDtoToView(inventory),
  });
  const blocking = rows
    .filter((r) => r.processing_status !== "ready" && !isDerivedFile(r.filename))
    .map((r) => ({ filename: r.filename, status: r.processing_status }));
  return { ready: blocking.length === 0, blocking };
}
